import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { Box, Tooltip } from '@mui/material';
import { Image, SlideshowInfinite, useItems } from '@scorenco/components';
import { RouteLink } from '@scorenco/core';
import { useRouter } from 'next/router';

import {
  GetClubSponsorsByCategoryDocument,
  GetClubSponsorsDocument,
} from './_internal/Sponsors.query.generated';

type SponsorsSliderWidgetProps = {
  clubId: Number;
};

export const SponsorsSliderWidget = ({ clubId }: SponsorsSliderWidgetProps) => {
  const router = useRouter();
  const speed = router.query.speed ? Number(router.query.speed) : 1;
  const height = router.query.height ? Number(router.query.height) : '8';
  const width = router.query.width ? Number(router.query.width) : '8';

  const getSponsors = useQuery(GetClubSponsorsDocument, {
    variables: {
      clubId,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    context: {
      role: 'anonymous',
    },
    skip: !clubId || !!Number(router.query.category),
  });

  const getSponsorsByCategory = useQuery(GetClubSponsorsByCategoryDocument, {
    variables: {
      clubId,
      cacheTTL: router.query.noCache ? 0 : undefined,
      categoryId: Number(router.query.category),
    },
    context: {
      role: 'anonymous',
    },
    skip: !clubId || !router.query.category,
  });

  const sponsors = useItems(
    router.query.category
      ? getSponsorsByCategory.data?.sponsors_sponsor
      : getSponsors.data?.sponsors_sponsor
  );

  return (
    <Box
      css={css`
        width: 100%;
        height: 10rem;
        margin-bottom: 1.5rem;
        background: transparent;
      `}
    >
      <SlideshowInfinite
        length={sponsors.length}
        elementRemSize={2 + Number(width)}
        speed={speed}
      >
        {[...sponsors]
          .sort(() => Math.random() - 0.5)
          .map((sponsor, index) => (
            <RouteLink key={index} href={sponsor?.url}>
              <Tooltip
                title={
                  sponsor?.description
                    ? `${sponsor?.name} : ${sponsor?.description}`
                    : sponsor?.name
                }
                followCursor
              >
                <a target="_blank">
                  <Image
                    src={sponsor?.logo}
                    loading="eager"
                    sx={{
                      height: `${height}rem`,
                      width: `${width}rem`,
                      objectFit: 'contain',
                      margin: '0 1rem',
                    }}
                  />
                </a>
              </Tooltip>
            </RouteLink>
          ))}
      </SlideshowInfinite>
    </Box>
  );
};
